// «Mon travail» — the consultant's landing screen. The right column names the
// NEXT ACT, not the phase (ux-design §10). Empty sections disappear.
import { useEffect, useState } from 'react';
import { api } from '../api/client';
import { useT } from '../i18n';
import { agoLabel, agoCount, formatDate } from '../utils/format';

export function MyWorkPage() {
  const { t, lang } = useT();
  const [data, setData] = useState<any | null>(null);
  const [recent, setRecent] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);

  const load = () => api.getMyWork().then(setData).catch(e => setError(e.message));
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (data && data.to_take.length === 0 && data.processes.length === 0) {
      api.getProcesses().then(all => setRecent(all.slice(0, 3))).catch(() => {});
    }
  }, [data]);

  if (error) return <div className="empty-state"><div className="empty-state-title">{error}</div></div>;
  if (!data) return <div className="page-loading"><div className="spinner" /></div>;

  const open = (id: number) => { window.location.hash = `#/process/${id}`; };
  const isEmpty = data.to_take.length === 0 && data.processes.length === 0 && data.returns.length === 0 && data.tickets.length === 0;

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">{t('shell.title.home')}</h1>
          <p className="page-meta">
            {t('mywork.headline', { active: data.stats.active, waiting: data.stats.waiting_governance })}
            {data.stats.max_governance_wait > 14 && <> · <span className="text-warn">{t('mywork.headlineWait', { n: data.stats.max_governance_wait })}</span></>}
          </p>
        </div>
      </div>

      {isEmpty && (
        <div className="empty-state">
          <div className="empty-state-title">{t('mywork.empty')}</div>
          {recent.length > 0 && (
            <div className="empty-state-desc">
              {t('mywork.emptyHint')}{' '}
              {recent.map(p => (
                <a key={p.id} className="text-accent" href={`#/process/${p.id}`} style={{ marginRight: 8 }}>
                  {p.process_code} {p.name}
                </a>
              ))}
            </div>
          )}
        </div>
      )}

      {data.to_take.length > 0 && (
        <section className="mb-24">
          <h3 className="section-label mb-12">{t('mywork.toTake')} ({data.to_take.length})</h3>
          <div className="flex flex-col gap-8">
            {data.to_take.map((p: any) => (
              <div key={p.id} className="card">
                <div className="flex justify-between items-center p-16">
                  <div className="list-row-clickable" style={{ cursor: 'pointer', flex: 1 }} onClick={() => open(p.id)}>
                    <span className="font-medium text-ink-1">{p.process_code} · {p.name}</span>
                    <div className="text-xs text-ink-3">
                      {p.pmo_project_id ? t('mywork.fromPmo', { when: agoCount(p.created_at, lang) }) : agoLabel(p.created_at, lang)}
                    </div>
                  </div>
                  <button className="btn btn-accent btn-sm" onClick={() => api.takeCharge(p.id).then(load)}>
                    {t('mywork.take')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {data.processes.length > 0 && (
        <section className="mb-24">
          <h3 className="section-label mb-12">{t('mywork.myProcesses')} ({data.processes.length})</h3>
          <table className="table">
            <tbody>
              {data.processes.map((p: any) => (
                <tr key={p.id} className="list-row-clickable" onClick={() => open(p.id)}>
                  <td style={{ width: 70 }} className="mono text-sm">{p.process_code}</td>
                  <td><span className="font-medium text-ink-1">{p.name}</span></td>
                  <td style={{ width: 170 }}>
                    <span className="state-chip state-chip--cycle">{t('rep.state.step', { n: p.phase_order })} · {lang === 'fr' ? p.phase_label_fr : p.phase_label_en}</span>
                  </td>
                  <td style={{ width: 320 }} className={`text-sm ${p.next.kind === 'waiting_governance' && (p.waiting_days ?? 0) > 14 ? 'text-warn' : 'text-ink-2'}`}>
                    {t(p.next.label_key, p.next.params)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {data.returns.length > 0 && (
        <section className="mb-24">
          <h3 className="section-label mb-12">{t('mywork.returns')} ({data.returns.length})</h3>
          <div className="flex flex-col gap-8">
            {data.returns.map((r: any) => (
              <div key={r.id} className="card card-clickable" onClick={() => open(r.id)}>
                <div className="p-16">
                  <span className="font-medium text-ink-1">{r.process_code} · {r.name}</span>{' '}
                  <span className="text-err text-sm">↩ {t('mywork.returnedBy', { name: r.by, date: formatDate(r.returned_at, lang) })}</span>
                  {r.motif && <div className="text-sm text-ink-2" style={{ marginTop: 4 }}>{t('mywork.motif', { motif: r.motif })}</div>}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {data.tickets.length > 0 && (
        <section className="mb-24">
          <h3 className="section-label mb-12">{t('mywork.myTickets')} ({data.tickets.length})</h3>
          <div className="flex flex-col gap-8">
            {data.tickets.map((tk: any) => (
              <div key={tk.id} className="card card-clickable" onClick={() => { window.location.hash = '#/tickets'; }}>
                <div className="flex justify-between items-center p-16">
                  <div>
                    <span className="font-medium text-ink-1">{tk.title}</span>
                    <div className="text-xs text-ink-3">{tk.process_code ? `${tk.process_code} · ${tk.process_name}` : ''}</div>
                  </div>
                  {tk.severity === 'high' && <span className="badge badge-err">{t('tk.severity.high')}</span>}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </>
  );
}
